import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, CHECKLIST_ITEMS, FUEL_LEVELS, PHOTO_ANGLES } from "../config";
import { Bike, BookOpen, CalendarClock, Camera, Check, ChevronLeft, ChevronRight, Eraser, Fuel, Gauge, Loader2, LogOut, MapPin, PackageCheck, PenLine, Phone, ShieldAlert, Trash2, Undo2, User, X } from "lucide-react";
import SiempriaCredit from "../components/SiempriaCredit";
import HelpModal from "../components/HelpModal";

const PICKUP_LABELS = { office: "Oficina · Costa Adeje", airport: "Aeropuerto", hotel: "Entrega en hotel" };

// ---- Canvas de firma táctil ----
function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111111";
  }, []);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return [(e.clientX - r.left) * (ref.current.width / r.width), (e.clientY - r.top) * (ref.current.height / r.height)];
  };
  const start = (e) => {
    drawing.current = true;
    ref.current.setPointerCapture(e.pointerId);
    const ctx = ref.current.getContext("2d");
    const [x, y] = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const [x, y] = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (empty) { setEmpty(false); onChange(false); }
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const ctx = ref.current.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, ref.current.width, ref.current.height);
    setEmpty(true);
    onChange(true);
  };

  return (
    <div>
      <div className="border-2 border-dashed border-topacio-ink/40 rounded-lg overflow-hidden bg-white">
        <canvas ref={ref} width={640} height={280} data-testid="signature-canvas"
          className="w-full h-44 touch-none cursor-crosshair"
          onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} />
      </div>
      <div className="flex justify-between items-center mt-2">
        <p className="text-[10px] font-bold text-topacio-faded uppercase tracking-wider">El cliente firma aquí con el dedo</p>
        <button type="button" data-testid="signature-clear" onClick={clear}
          className="flex items-center gap-1.5 text-xs font-extrabold text-topacio-faded hover:text-topacio-red transition-colors">
          <Eraser className="w-3.5 h-3.5" /> Limpiar
        </button>
      </div>
    </div>
  );
}

const canvasPngBlob = (hostEl) => new Promise((resolve) => {
  hostEl?.querySelector("canvas")?.toBlob((b) => resolve(b), "image/png");
});

// ---- Fotos guiadas por ángulo con cámara (protocolo de estado) ----
function AnglePhotos({ photos, setPhotos, max = 8, testPrefix = "angle" }) {
  const refs = useRef({});
  const extraRef = useRef(null);
  const setAnglePhoto = (angle, file) => {
    const rest = photos.filter((p) => p.angle !== angle);
    setPhotos(file ? [...rest, { angle, file }] : rest);
  };
  const extras = photos.filter((p) => p.angle === "extra");

  const captureButton = (angle, label, tid) => {
    const photo = photos.find((p) => p.angle === angle);
    return (
      <div key={angle}>
        {photo ? (
          <div className="relative aspect-[4/3] rounded-lg border-2 border-topacio-ink overflow-hidden bg-white">
            <img src={URL.createObjectURL(photo.file)} alt={label} className="w-full h-full object-cover" />
            <span className="absolute bottom-1 left-1 badge-brutal bg-topacio-ink text-white !text-[9px]">{label}</span>
            <button type="button" data-testid={`${testPrefix}-remove-${angle}`} onClick={() => setAnglePhoto(angle, null)}
              className="absolute top-1 right-1 w-6 h-6 bg-topacio-red text-white rounded-md flex items-center justify-center border border-black/30">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button type="button" data-testid={`${testPrefix}-capture-${angle}`}
            onClick={() => refs.current[angle]?.click()}
            className="w-full aspect-[4/3] rounded-lg border-2 border-dashed border-topacio-ink/40 flex flex-col items-center justify-center gap-1 text-topacio-faded hover:text-topacio-ink hover:border-topacio-ink hover:bg-brand/30 transition-colors">
            <Camera className="w-6 h-6" />
            <span className="text-[10px] font-extrabold uppercase tracking-wider">{label}</span>
          </button>
        )}
        <input ref={(el) => { refs.current[angle] = el; }} type="file" accept="image/*" capture="environment" className="hidden"
          data-testid={`${testPrefix}-input-${angle}`}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) setAnglePhoto(angle, f); e.target.value = ""; }} />
      </div>
    );
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        {PHOTO_ANGLES.map(([a, label]) => captureButton(a, label))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        {extras.map((p, i) => (
          <div key={i} className="relative w-16 h-16 rounded-lg border-2 border-topacio-ink overflow-hidden">
            <img src={URL.createObjectURL(p.file)} alt={`extra ${i + 1}`} className="w-full h-full object-cover" />
            <button type="button" data-testid={`${testPrefix}-remove-extra-${i}`}
              onClick={() => setPhotos(photos.filter((ph) => ph !== p))}
              className="absolute top-0.5 right-0.5 w-5 h-5 bg-topacio-red text-white rounded-md flex items-center justify-center">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        {photos.length < max && (
          <button type="button" data-testid={`${testPrefix}-extra-btn`} onClick={() => extraRef.current?.click()}
            className="h-16 px-3 rounded-lg border-2 border-dashed border-topacio-ink/40 text-[10px] font-extrabold uppercase text-topacio-faded hover:text-topacio-ink hover:border-topacio-ink transition-colors">
            + Detalle / daño
          </button>
        )}
        <input ref={extraRef} type="file" accept="image/*" capture="environment" className="hidden"
          data-testid={`${testPrefix}-extra-input`}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) setPhotos([...photos, { angle: "extra", file: f }]); e.target.value = ""; }} />
      </div>
    </div>
  );
}

// ---- Checklist del vehículo (estilo rent-a-car) — editable desde el panel de Contenido ----
function ChecklistFields({ value, onChange, testPrefix }) {
  const [items, setItems] = useState(CHECKLIST_ITEMS);
  useEffect(() => {
    axios.get(`${API}/content`).then(({ data }) => {
      const dp = data?.delivery_protocol;
      if (Array.isArray(dp) && dp.length) setItems(dp.map((x) => [x.key, x.label]));
    }).catch(() => {});
  }, []);
  return (
    <div className="grid grid-cols-1 gap-1.5">
      {items.map(([key, label]) => (
        <label key={key} className="flex items-center gap-3 cursor-pointer select-none bg-white border-2 border-topacio-ink/15 rounded-lg px-3 py-2.5">
          <input type="checkbox" data-testid={`${testPrefix}-check-${key}`}
            checked={!!value[key]} onChange={(e) => onChange({ ...value, [key]: e.target.checked })}
            className="w-5 h-5 accent-[#FFCC00] shrink-0" />
          <span className="text-xs font-bold text-topacio-ink">{label}</span>
        </label>
      ))}
    </div>
  );
}

// ---- Kilómetros y combustible ----
function KmFuelFields({ km, fuel, setKm, setFuel, testPrefix }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded flex items-center gap-1"><Gauge className="w-3 h-3" /> Kilómetros</label>
        <input data-testid={`${testPrefix}-km`} type="number" min="0" inputMode="numeric" value={km} onChange={(e) => setKm(e.target.value)}
          placeholder="ej. 12345" className="input-brutal mt-1" />
      </div>
      <div>
        <label className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded flex items-center gap-1"><Fuel className="w-3 h-3" /> Combustible</label>
        <select data-testid={`${testPrefix}-fuel`} value={fuel} onChange={(e) => setFuel(e.target.value)} className="input-brutal mt-1">
          <option value="">—</option>
          {FUEL_LEVELS.map((f) => <option key={f} value={f}>{f === "reserva" ? "Reserva" : f === "lleno" ? "Lleno" : f}</option>)}
        </select>
      </div>
    </div>
  );
}

// ---- Datos de la reserva (compartido por ambos partes) ----
function BookingSummary({ bk }) {
  return (
    <div className="card-brutal !shadow-none p-4 space-y-2">
      <div className="flex items-center gap-2 text-sm font-bold text-topacio-ink"><Bike className="w-4 h-4 text-topacio-red" /> {bk.bike_name}</div>
      <div className="flex items-center gap-2 text-sm text-topacio-faded"><User className="w-4 h-4" /> {bk.name}</div>
      {bk.phone && (
        <a data-testid="delivery-call-link" href={`tel:${bk.phone}`} className="flex items-center gap-2 text-sm font-bold text-topacio-green">
          <Phone className="w-4 h-4" /> {bk.phone}
        </a>
      )}
      <div className="flex items-center gap-2 text-xs text-topacio-faded"><MapPin className="w-4 h-4" />
        {PICKUP_LABELS[bk.pickup] || bk.pickup}{bk.pickup === "hotel" && bk.hotel_name ? `: ${bk.hotel_name}` : ""}
      </div>
      <div className="text-xs text-topacio-faded font-semibold">{bk.start_date} → {bk.end_date}</div>
      {bk.precheckin_status === "pending" && (
        <div data-testid="precheckin-pending-line" className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider text-topacio-red">
          <ShieldAlert className="w-3.5 h-3.5" /> Documentación de check-in pendiente
        </div>
      )}
      {bk.precheckin_status === "completed" && (
        <div data-testid="precheckin-done-line" className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider text-topacio-green">
          <Check className="w-3.5 h-3.5" /> Documentación ya en la ficha
        </div>
      )}
      {bk.message && <div className="text-xs bg-topacio-sand border border-topacio-ink/20 rounded-md px-3 py-2 font-semibold text-topacio-ink">Nota: {bk.message}</div>}
    </div>
  );
}

// ---- Parte de ENTREGA: protocolo de estado + firma ----
function DeliverySheet({ bk, headers, onClose, onDone }) {
  const [explanation, setExplanation] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [km, setKm] = useState("");
  const [fuel, setFuel] = useState("");
  const [photos, setPhotos] = useState([]);
  const [override, setOverride] = useState(false);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [busy, setBusy] = useState(false);
  const canvasHost = useRef(null);

  const covered = PHOTO_ANGLES.filter(([a]) => photos.some((p) => p.angle === a)).length;
  const photosOk = covered === PHOTO_ANGLES.length;
  const canSubmit = !busy && !sigEmpty && explanation && (photosOk || override);

  const submit = async () => {
    setBusy(true);
    try {
      const blob = await canvasPngBlob(canvasHost.current);
      const fd = new FormData();
      fd.append("signature", blob, "firma.png");
      fd.append("explanation", String(explanation));
      fd.append("km", km);
      fd.append("fuel", fuel);
      fd.append("checklist", JSON.stringify(checklist));
      fd.append("angles", JSON.stringify(photos.map((p) => p.angle)));
      photos.forEach((p) => fd.append("photos", p.file, `${p.angle}.jpg`));
      await axios.post(`${API}/admin/bookings/${bk.ref}/delivery-acta`, fd, { headers });
      toast.success("Entrega registrada · protocolo y acta en la ficha del cliente");
      onDone();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "already_delivered") { toast.error("Esta reserva ya tiene el acta firmada"); onDone(); onClose(); }
      else toast.error(d === "too_many_photos" ? "Máximo 8 fotos"
        : d === "too_big" ? "Alguna foto supera 10 MB"
        : d === "bad_type" ? "Las fotos deben ser imágenes (jpg, png, webp)"
        : "Error al registrar la entrega");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center" data-testid="delivery-sheet-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border-t-4 sm:border-2 border-topacio-ink sm:rounded-xl shadow-[8px_8px_0_0_#111] w-full sm:max-w-lg max-h-[92vh] overflow-y-auto" data-lenis-prevent>
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-topacio-ink sticky top-0 bg-white z-10">
          <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2">
            <PenLine className="w-5 h-5 text-topacio-red" /> Protocolo de entrega · <span className="font-mono text-topacio-red">{bk.ref}</span>
          </h3>
          <button data-testid="delivery-sheet-close" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          <BookingSummary bk={bk} />

          <div>
            <p className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded mb-2 flex items-center gap-1.5">
              <Camera className="w-4 h-4" /> Fotos del estado ({covered}/4 ángulos)
            </p>
            <AnglePhotos photos={photos} setPhotos={setPhotos} testPrefix="delivery-angle" />
            <p className="text-[10px] text-topacio-faded font-semibold mt-1.5">
              Se guardan 90 días para compararlas con la devolución. Las actas no se borran nunca.
            </p>
            {!photosOk && (
              <label className="flex items-start gap-2.5 mt-2 cursor-pointer select-none bg-brand/30 border-2 border-dashed border-topacio-ink/40 rounded-lg px-3 py-2.5">
                <input data-testid="photos-override" type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)}
                  className="w-5 h-5 accent-[#E63946] shrink-0 mt-0.5" />
                <span className="text-[11px] font-bold text-topacio-ink">
                  Faltan ángulos: entregar igualmente (queda registrado en el acta)
                </span>
              </label>
            )}
          </div>

          <div>
            <p className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded mb-2">Revisión del vehículo</p>
            <ChecklistFields value={checklist} onChange={setChecklist} testPrefix="delivery" />
          </div>

          <KmFuelFields km={km} fuel={fuel} setKm={setKm} setFuel={setFuel} testPrefix="delivery" />

          <label className="flex items-start gap-3 cursor-pointer select-none card-brutal !shadow-none p-4">
            <input data-testid="explanation-checkbox" type="checkbox" checked={explanation} onChange={(e) => setExplanation(e.target.checked)}
              className="w-6 h-6 mt-0.5 accent-[#FFCC00] shrink-0" />
            <span className="text-sm font-bold text-topacio-ink">He explicado al cliente el funcionamiento de la moto
              <span className="block text-xs font-semibold text-topacio-faded mt-0.5">mandos, luces, caballete, cascos y documentación</span>
            </span>
          </label>

          <div ref={canvasHost}>
            <SignaturePad onChange={(isEmpty) => setSigEmpty(isEmpty)} />
          </div>

          <button data-testid="delivery-submit-btn" onClick={submit} disabled={!canSubmit}
            className="btn-primary w-full !py-4 !text-sm disabled:opacity-50">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
            Firmar y registrar entrega
          </button>
          {!canSubmit && !busy && (
            <p className="text-[10px] text-center font-bold text-topacio-faded uppercase tracking-wider">
              {!explanation ? "Marca la explicación" : sigEmpty ? "Falta la firma del cliente" : !photosOk && !override ? "Faltan fotos de ángulo (o confirma la excepción)" : ""}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Parte de DEVOLUCIÓN: comparación con la entrega, estado, fotos, fianza y firma ----
function ReturnSheet({ bk, headers, onClose, onDone }) {
  const token = localStorage.getItem("admin_token");
  const [condition, setCondition] = useState(null);
  const [vehicleOk, setVehicleOk] = useState(true);
  const [checklist, setChecklist] = useState({});
  const [km, setKm] = useState("");
  const [fuel, setFuel] = useState("");
  const [decision, setDecision] = useState("devuelta");
  const [retained, setRetained] = useState("");
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [busy, setBusy] = useState(false);
  const canvasHost = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/bookings/${bk.ref}/condition`, { headers })
      .then(({ data }) => setCondition(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bk.ref]);

  const deliveryPhotos = (condition?.photos || []).filter((p) => p.kind === "entrega");
  const deliveryKm = condition?.booking?.delivery_km;
  const kmDiff = deliveryKm && km ? Math.max(0, parseInt(km, 10) - parseInt(deliveryKm, 10)) : null;

  const submit = async () => {
    setBusy(true);
    try {
      const blob = await canvasPngBlob(canvasHost.current);
      const fd = new FormData();
      fd.append("signature", blob, "firma.png");
      fd.append("vehicle_ok", String(vehicleOk));
      fd.append("deposit_decision", decision);
      fd.append("retained", decision === "parcial" ? String(parseFloat(retained) || 0) : "0");
      fd.append("note", note);
      fd.append("km", km);
      fd.append("fuel", fuel);
      fd.append("checklist", JSON.stringify(checklist));
      photos.forEach((p) => fd.append("photos", p.file, `${p.angle}.jpg`));
      await axios.post(`${API}/admin/bookings/${bk.ref}/return-acta`, fd, { headers });
      toast.success("Devolución cerrada · acta y fotos en la ficha del cliente");
      onDone();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "already_returned") { toast.error("Esta reserva ya tiene la devolución firmada"); onDone(); onClose(); }
      else toast.error(d === "retained_required" ? "Indica el importe retenido"
        : d === "too_big" ? "Alguna foto supera 10 MB"
        : d === "bad_type" ? "Las fotos deben ser imágenes (jpg, png, webp)"
        : "Error al registrar la devolución");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center" data-testid="return-sheet-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border-t-4 sm:border-2 border-topacio-ink sm:rounded-xl shadow-[8px_8px_0_0_#111] w-full sm:max-w-lg max-h-[92vh] overflow-y-auto" data-lenis-prevent>
        <div className="flex items-center justify-between px-5 py-4 border-b-2 border-topacio-ink sticky top-0 bg-white z-10">
          <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2">
            <Undo2 className="w-5 h-5 text-topacio-red" /> Parte de devolución · <span className="font-mono text-topacio-red">{bk.ref}</span>
          </h3>
          <button data-testid="return-sheet-close" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          <BookingSummary bk={bk} />

          {/* Comparación con la entrega */}
          <div data-testid="condition-delivery-summary" className="border-2 border-topacio-ink rounded-lg overflow-hidden">
            <div className="bg-topacio-ink text-white text-[10px] font-extrabold uppercase tracking-wider px-3 py-2 flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5 text-brand" /> Así salió en la entrega
            </div>
            <div className="p-3 bg-topacio-sand">
              {!condition ? (
                <p className="text-xs text-topacio-faded font-semibold flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando protocolo de entrega…</p>
              ) : (
                <>
                  {(deliveryKm || condition.booking?.delivery_fuel) && (
                    <p className="text-xs font-bold text-topacio-ink mb-2">
                      Salió con {deliveryKm ? `${deliveryKm} km` : "km sin anotar"}{condition.booking?.delivery_fuel ? ` · combustible: ${condition.booking.delivery_fuel}` : ""}
                    </p>
                  )}
                  {deliveryPhotos.length > 0 ? (
                    <div className="grid grid-cols-4 gap-1.5" data-testid="condition-delivery-photos">
                      {deliveryPhotos.map((p) => (
                        <a key={p.id} href={`${API}/admin/bookings/${bk.ref}/photo/${p.id}?auth=${encodeURIComponent(token)}`}
                          target="_blank" rel="noreferrer" data-testid={`photo-entrega-${p.id}`}
                          className="relative aspect-square rounded-md border-2 border-topacio-ink overflow-hidden bg-white block">
                          <img src={`${API}/admin/bookings/${bk.ref}/photo/${p.id}?auth=${encodeURIComponent(token)}`}
                            alt={p.angle} loading="lazy" className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] font-extrabold uppercase text-center py-0.5">
                            {(PHOTO_ANGLES.find(([a]) => a === p.angle) || [null, p.angle || "extra"])[1]}
                          </span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] font-bold text-topacio-faded">Sin fotos de la entrega (o ya purgadas · se conservan {condition.retention_days} días).</p>
                  )}
                </>
              )}
            </div>
          </div>

          {bk.deposit_amount != null && (
            <div className="text-xs font-extrabold text-topacio-ink bg-topacio-sand border-2 border-topacio-ink/20 rounded-lg px-3 py-2">
              Fianza registrada: {bk.deposit_amount}€{bk.deposit_method ? ` · ${bk.deposit_method}` : ""}
            </div>
          )}

          <div>
            <p className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded mb-2">Revisión a la devolución</p>
            <ChecklistFields value={checklist} onChange={setChecklist} testPrefix="return" />
          </div>

          <KmFuelFields km={km} fuel={fuel} setKm={setKm} setFuel={setFuel} testPrefix="return" />
          {kmDiff != null && kmDiff > 0 && (
            <p data-testid="km-diff" className="text-xs font-extrabold text-topacio-ink bg-brand/40 border-2 border-topacio-ink rounded-lg px-3 py-2">
              Ha rodado {kmDiff} km en este alquiler
            </p>
          )}

          <label className="flex items-start gap-3 cursor-pointer select-none card-brutal !shadow-none p-4">
            <input data-testid="vehicle-ok-checkbox" type="checkbox" checked={vehicleOk} onChange={(e) => setVehicleOk(e.target.checked)}
              className="w-6 h-6 mt-0.5 accent-[#FFCC00] shrink-0" />
            <span className="text-sm font-bold text-topacio-ink">El vehículo vuelve en buen estado
              <span className="block text-xs font-semibold text-topacio-faded mt-0.5">compáralo con las fotos de la entrega de arriba</span>
            </span>
          </label>

          <div>
            <p className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded mb-2">Fotos de la devolución (máx. 8)</p>
            <AnglePhotos photos={photos} setPhotos={setPhotos} testPrefix="return-angle" />
          </div>

          <div>
            <p className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded mb-2">Fianza</p>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="deposit-return-full" onClick={() => setDecision("devuelta")}
                className={`px-3 py-3 rounded-lg border-2 border-topacio-ink text-xs font-extrabold uppercase tracking-wider transition-colors ${decision === "devuelta" ? "bg-topacio-green text-white shadow-[3px_3px_0_0_#111]" : "bg-white text-topacio-faded"}`}>
                Devolver íntegra
              </button>
              <button type="button" data-testid="deposit-return-partial" onClick={() => setDecision("parcial")}
                className={`px-3 py-3 rounded-lg border-2 border-topacio-ink text-xs font-extrabold uppercase tracking-wider transition-colors ${decision === "parcial" ? "bg-topacio-red text-white shadow-[3px_3px_0_0_#111]" : "bg-white text-topacio-faded"}`}>
                Retener por daños
              </button>
            </div>
            {decision === "parcial" && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded">Importe retenido (€)</label>
                  <input data-testid="retained-amount" type="number" min="0" step="0.01" value={retained} onChange={(e) => setRetained(e.target.value)} className="input-brutal mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded">Motivo</label>
                  <input data-testid="retained-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Arañazo lateral derecho…" className="input-brutal mt-1" />
                </div>
              </div>
            )}
          </div>

          <div ref={canvasHost}>
            <SignaturePad onChange={(isEmpty) => setSigEmpty(isEmpty)} />
          </div>

          <button data-testid="return-submit-btn" onClick={submit} disabled={busy || sigEmpty || (decision === "parcial" && !(parseFloat(retained) > 0))}
            className="btn-primary w-full !py-4 !text-sm disabled:opacity-50">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <PackageCheck className="w-5 h-5" />}
            Firmar y cerrar devolución
          </button>
          {sigEmpty && (
            <p className="text-[10px] text-center font-bold text-topacio-faded uppercase tracking-wider">Recoge la firma del cliente para cerrar</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DeliveriesPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("admin_token");
  const role = localStorage.getItem("admin_role");
  const headers = { Authorization: `Bearer ${token}` };
  const [date, setDate] = useState(() => new Date().toLocaleDateString("sv-SE")); // YYYY-MM-DD local
  const [data, setData] = useState(null);
  const [view, setView] = useState("entregas");
  const [open, setOpen] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const shift = (days) => {
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + days);
    setDate(d.toLocaleDateString("sv-SE"));
  };

  const load = () => {
    axios.get(`${API}/admin/deliveries`, { headers, params: { date } })
      .then(({ data: d }) => setData(d))
      .catch((e) => {
        if (e.response?.status === 401 || e.response?.status === 403) {
          localStorage.removeItem("admin_token");
          navigate("/admin/login");
        }
      });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token || !["entrega", "admin", "superadmin"].includes(role)) { navigate("/admin/login"); return; }
    load();
  }, [date]);

  const logout = () => { localStorage.removeItem("admin_token"); navigate("/admin/login"); };
  const deliveries = data?.deliveries || [];
  const returns = data?.returns || [];
  const items = view === "entregas" ? deliveries : returns;
  const pendingOf = (lst, flag) => lst.filter((d) => d[flag] !== (flag === "return_status" ? "devuelta" : "entregada")).length;
  const pendDel = pendingOf(deliveries, "delivery_status");
  const pendRet = pendingOf(returns, "return_status");
  const isToday = date === new Date().toLocaleDateString("sv-SE");

  return (
    <div className="min-h-screen bg-topacio-sand" data-testid="deliveries-page">
      <header className="border-b-2 border-topacio-ink bg-white sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-topacio.jpg" alt="Topacio" className="h-9 w-auto rounded border-2 border-topacio-ink" />
            <span className="font-display font-bold text-sm text-topacio-ink">ENTREGAS</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="help-btn" onClick={() => setHelpOpen(true)} className="text-topacio-faded hover:text-topacio-ink transition-colors" title="Manual de usuario">
              <BookOpen className="w-4 h-4" />
            </button>
            {["admin", "superadmin"].includes(role) && (
              <button data-testid="back-to-panel-btn" onClick={() => navigate("/admin")} className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded hover:text-topacio-ink transition-colors">
                Panel
              </button>
            )}
            <button data-testid="deliveries-logout-btn" onClick={logout} className="flex items-center gap-2 text-xs font-bold text-topacio-faded hover:text-topacio-ink transition-colors">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <button data-testid="date-prev" onClick={() => shift(-1)} className="w-10 h-10 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-brand transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <div data-testid="deliveries-date" className="font-display font-black text-lg text-topacio-ink flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-topacio-red" /> {isToday ? "Hoy" : date}
            </div>
            <div className="text-[11px] font-bold text-topacio-faded uppercase tracking-wider">{isToday ? date : ""}</div>
          </div>
          <button data-testid="date-next" onClick={() => shift(1)} className="w-10 h-10 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-brand transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-6">
          <button data-testid="view-entregas" onClick={() => setView("entregas")}
            className={`px-3 py-2.5 rounded-lg border-2 border-topacio-ink text-xs font-extrabold uppercase tracking-wider transition-colors ${view === "entregas" ? "bg-brand text-topacio-ink shadow-[3px_3px_0_0_#111]" : "bg-white text-topacio-faded"}`}>
            Entregas · {pendDel} pend.
          </button>
          <button data-testid="view-devoluciones" onClick={() => setView("devoluciones")}
            className={`px-3 py-2.5 rounded-lg border-2 border-topacio-ink text-xs font-extrabold uppercase tracking-wider transition-colors ${view === "devoluciones" ? "bg-brand text-topacio-ink shadow-[3px_3px_0_0_#111]" : "bg-white text-topacio-faded"}`}>
            Devoluciones · {pendRet} pend.
          </button>
        </div>

        {!data ? (
          <p className="text-center text-topacio-faded text-sm flex items-center justify-center gap-2 py-16"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</p>
        ) : items.length === 0 ? (
          <div data-testid="deliveries-empty" className="card-brutal p-10 text-center">
            <Bike className="w-8 h-8 mx-auto text-topacio-faded mb-3" />
            <p className="font-bold text-topacio-ink">Sin {view === "entregas" ? "entregas" : "devoluciones"} {isToday ? "hoy" : "ese día"}</p>
            <p className="text-xs text-topacio-faded font-semibold mt-1">
              {view === "entregas" ? "Las recogidas de cada día aparecen aquí automáticamente." : "Aquí aparecen las motos entregadas que vuelven ese día."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((bk) => {
              const done = view === "entregas" ? bk.delivery_status === "entregada" : bk.return_status === "devuelta";
              return (
                <button key={bk.ref} data-testid={`delivery-card-${bk.ref}`} disabled={done}
                  onClick={() => setOpen(bk)}
                  className={`w-full text-left card-brutal p-5 transition-all ${done ? "opacity-60" : "hover:-translate-y-0.5"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display font-black text-topacio-ink text-lg leading-tight">{bk.bike_name}</div>
                      <div className="text-sm font-bold text-topacio-ink mt-1">{bk.name}</div>
                      <div className="text-xs text-topacio-faded font-semibold mt-0.5">
                        {PICKUP_LABELS[bk.pickup] || bk.pickup}{bk.pickup === "hotel" && bk.hotel_name ? `: ${bk.hotel_name}` : ""}
                        {view === "entregas" ? ` · devuelve ${bk.end_date}` : ` · recogió ${bk.start_date}`}
                      </div>
                      {view === "devoluciones" && bk.deposit_amount != null && (
                        <div className="text-[10px] font-extrabold text-topacio-ink mt-1 uppercase tracking-wider">
                          Fianza: {bk.deposit_amount}€{bk.deposit_method ? ` · ${bk.deposit_method}` : ""}
                        </div>
                      )}
                    </div>
                    {done ? (
                      <span data-testid={`delivery-done-${bk.ref}`} className="badge-brutal bg-topacio-green text-white shrink-0">
                        {view === "entregas" ? "Entregada" : "Devuelta"}
                      </span>
                    ) : (
                      <span className="badge-brutal bg-brand text-topacio-ink shrink-0">Pendiente</span>
                    )}
                  </div>
                  {done && view === "entregas" && bk.delivered_by && (
                    <div className="text-[10px] text-topacio-faded font-bold mt-2">Firmada por {bk.delivered_by} · {(bk.delivered_at || "").slice(5, 16).replace("T", " ")}</div>
                  )}
                  {done && view === "devoluciones" && bk.returned_by && (
                    <div className="text-[10px] text-topacio-faded font-bold mt-2">Cerrada por {bk.returned_by} · {(bk.returned_at || "").slice(5, 16).replace("T", " ")}</div>
                  )}
                  {!done && bk.phone && (
                    <div className="text-xs font-bold text-topacio-green mt-2 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {bk.phone}</div>
                  )}
                </button>
              );
            })}
          </div>
        )}
        <div className="mt-10 text-center"><SiempriaCredit /></div>
      </main>

      {open && view === "entregas" && <DeliverySheet bk={open} headers={headers} onClose={() => setOpen(null)} onDone={load} />}
      {open && view === "devoluciones" && <ReturnSheet bk={open} headers={headers} onClose={() => setOpen(null)} onDone={load} />}
      {helpOpen && <HelpModal role={role} onClose={() => setHelpOpen(false)} />}
    </div>
  );
}
